import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { ReactElement, useEffect, useState } from 'react';

import Breadcrumbs from 'src/components/Breadcrumbs';
import { Cookies } from 'react-cookie-consent';
import ReactGA from 'react-ga4';
import RootWidgetLayout from 'src/components/layouts/RootWidgetLayout';
import Trans from 'next-translate/Trans';
import { Translate } from 'next-translate';
import styled from '@emotion/styled';
import theme from 'src/themes';
import useTranslation from 'next-translate/useTranslation';

const CookiesContainer = styled('div')(() => ({
  maxWidth: theme.breakpoints.values.lg,
  margin: 'auto',
  padding: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2, 2),
  },
}));

const CookiesPolicy = () => {
  const { t } = useTranslation();

  const [cookies, setCookies] = useState('false');
  useEffect(() => {
    const getCookies = Cookies.get();
    setCookies(getCookies?.CookieConsent || 'false');
  }, []);

  const handleCookieChoice = () => {
    if (cookies === 'true') {
      Cookies.set('CookieConsent', 'true');
      ReactGA.gtag('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
        security_storage: 'granted',
      });
    } else if (cookies === 'false') {
      Cookies.set('CookieConsent', 'false');
      ReactGA.gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'denied',
      });
    }
    window.location.reload();
  };

  return (
    <Box style={{ backgroundColor: theme.palette.common.white }}>
      <Breadcrumbs
        breadcrumbs={[
          {
            text: t('breadcrumbs.home'),
            href: '/',
          },
          {
            text: 'Cookies',
            disabled: true,
          },
        ]}
      />
      <Grid direction="column" container>
        <Grid item>
          <CookiesContainer>
            <Typography
              component="h5"
              variant="h5"
              sx={{ marginBottom: theme.spacing(3) }}
            >
              {t('cookies.policy.header')}
            </Typography>
            <Typography
              component="h6"
              variant="h6"
              sx={{
                marginBottom: theme.spacing(0),
                marginTop: theme.spacing(3),
              }}
            >
              {t('cookies.policy.useOfCookiesHeader')}
            </Typography>
            <Trans
              i18nKey="cookies.policy.useOfCookiesBody"
              components={{
                ul: <ul />,
                li: <li />,
                p: <p />,
              }}
            />
            <Typography
              component="h6"
              variant="h6"
              sx={{
                marginBottom: theme.spacing(0),
                marginTop: theme.spacing(3),
              }}
            >
              {t('cookies.policy.whatCookiesHeader')}
            </Typography>
            <Trans
              i18nKey="cookies.policy.whatCookiesBody"
              components={{
                ul: <ul />,
                li: <li />,
                p: <p />,
              }}
            />
            <Typography
              component="h6"
              variant="h6"
              sx={{
                marginBottom: theme.spacing(0),
                marginTop: theme.spacing(3),
              }}
            >
              {t('cookies.policy.settingsHeader')}
            </Typography>
            <Trans
              i18nKey="cookies.policy.settingsBody"
              components={{
                ul: <ul />,
                li: <li />,
                p: <p />,
              }}
            />
            <Typography
              component="h6"
              variant="h6"
              sx={{
                marginBottom: theme.spacing(0),
                marginTop: theme.spacing(3),
              }}
            >
              {t('cookies.policy.additionalCookiesHeader')}
            </Typography>
            <Trans
              i18nKey="cookies.policy.additionalCookiesBody"
              components={{
                ul: <ul />,
                li: <li />,
                p: <p />,
              }}
            />
            <RadioGroup
              value={cookies}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCookies((e.target as HTMLInputElement).value)
              }
            >
              <FormControlLabel
                value="true"
                control={<Radio />}
                label={t('cookies.policy.additionalCookiesAcceptRadio')}
              />
              <FormControlLabel
                value="false"
                control={<Radio />}
                label={t('cookies.policy.additionalCookiesDeclineRadio')}
              />
            </RadioGroup>
            <Typography
              component="h6"
              variant="h6"
              sx={{
                marginBottom: theme.spacing(0),
                marginTop: theme.spacing(3),
              }}
            >
              {t('cookies.policy.essentialCookiesHeader')}
            </Typography>
            <Typography component="p" variant="body1">
              {t('cookies.policy.essentialCookiesBody')}
            </Typography>
            <Button variant="contained" onClick={handleCookieChoice}>
              {t('cookies.policy.saveButton')}
            </Button>
            <Typography
              component="h6"
              variant="h6"
              sx={{
                marginBottom: theme.spacing(0),
                marginTop: theme.spacing(3),
              }}
            >
              {t('cookies.policy.changesHeader')}
            </Typography>
            <Trans
              i18nKey="cookies.policy.changesBody"
              components={{
                p: <p />,
              }}
            />
            <address>
              <p>
                ORCHA Health,
                <br />
                The Innovation Centre,
                <br />
                Sci-Tech Daresbury,
                <br />
                Keckwick Lane,
                <br />
                Daresbury,
                <br />
                Warrington
                <br />
                WA4 4FS,
                <br />
                United Kingdom
              </p>
            </address>
            <address>
              <p>
                ORCHA,
                <br />
                CIC,
                <br />
                16th Floor,
                <br />
                50 Milk Street,
                <br />
                Boston,
                <br />
                MA 02109,
                <br />
                USA
              </p>
            </address>
            <p>
              {t('cookies.policy.email')}:{' '}
              <a href="mailto:hello@orchahealth.com">hello@orchahealth.com</a>
            </p>
          </CookiesContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export const getServerSideProps = async () => {
  const props = {};
  return {
    props,
  };
};

CookiesPolicy.getLayout = (page: ReactElement, t: Translate) => {
  return (
    <RootWidgetLayout pageTitle={t('cookies.policy.pageTitle')}>
      {page}
    </RootWidgetLayout>
  );
};

export default CookiesPolicy;
